import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, GripVertical, Save, Eye, 
  CheckSquare, Circle, ListOrdered, ToggleLeft, MessageSquare,
  Grid3X3, ChevronDown, Settings, Copy, FileText, Sparkles, LayoutTemplate,
  Briefcase, GraduationCap, Calendar, Users, Package
} from 'lucide-react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const CATEGORY_ICONS = {
  'Business': Briefcase,
  'HR': Users,
  'Academic': GraduationCap,
  'Events': Calendar,
  'default': LayoutTemplate
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { value: 'demographic', label: 'Demographic', icon: Settings, description: 'Age, gender, education, etc.' },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: CheckSquare, description: 'Select one option' },
  { value: 'likert', label: 'Likert Scale', icon: ListOrdered, description: 'Agreement/satisfaction scale' },
  { value: 'ranked', label: 'Ranked', icon: ListOrdered, description: 'Order items by preference' },
  { value: 'dichotomous', label: 'Yes/No', icon: ToggleLeft, description: 'Binary choice question' },
  { value: 'open_ended', label: 'Open Ended', icon: MessageSquare, description: 'Free text response' },
  { value: 'matrix', label: 'Matrix', icon: Grid3X3, description: 'Rate multiple items on same scale' },
  { value: 'dropdown', label: 'Dropdown', icon: ChevronDown, description: 'Select from dropdown list' }
];

const DEFAULT_LIKERT_LABELS = {
  1: 'Strongly Disagree',
  2: 'Disagree',
  3: 'Neutral',
  4: 'Agree',
  5: 'Strongly Agree'
};

// Predefined demographic options
const DEMOGRAPHIC_PRESETS = {
  age: {
    label: 'Age Range',
    question: 'What is your age?',
    options: ['Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65 or older']
  },
  gender: {
    label: 'Gender',
    question: 'What is your gender?',
    options: ['Male', 'Female', 'Non-binary', 'Prefer not to say']
  },
  education: {
    label: 'Education Level',
    question: 'What is your highest level of education?',
    options: ['High School', 'Some College', 'Associate Degree', 'Bachelor\'s Degree', 'Master\'s Degree', 'Doctorate', 'Other']
  },
  income: {
    label: 'Income Range',
    question: 'What is your annual household income?',
    options: ['Under $25,000', '$25,000-$49,999', '$50,000-$74,999', '$75,000-$99,999', '$100,000-$149,999', '$150,000 or more', 'Prefer not to say']
  },
  employment: {
    label: 'Employment Status',
    question: 'What is your current employment status?',
    options: ['Employed full-time', 'Employed part-time', 'Self-employed', 'Unemployed', 'Student', 'Retired', 'Other']
  },
  marital: {
    label: 'Marital Status',
    question: 'What is your marital status?',
    options: ['Single', 'Married', 'Divorced', 'Widowed', 'Separated', 'Prefer not to say']
  },
  custom: {
    label: 'Custom',
    question: '',
    options: ['Option 1', 'Option 2']
  }
};

const SurveyBuilderPage = () => {
  const { projectId, surveyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(!!surveyId);
  const [saving, setSaving] = useState(false);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetResponses, setTargetResponses] = useState('');
  const [questions, setQuestions] = useState([]);
  const [addQuestionOpen, setAddQuestionOpen] = useState(false);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API}/survey-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(res.data);
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    };
    fetchTemplates();
  }, [token]);

  useEffect(() => {
    if (surveyId) {
      const fetchSurvey = async () => {
        try {
          const res = await axios.get(`${API}/surveys/${surveyId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTitle(res.data.title);
          setDescription(res.data.description || '');
          setTargetResponses(res.data.target_responses?.toString() || '');
          setQuestions(res.data.questions || []);
        } catch (err) {
          toast.error('Failed to load survey');
          navigate(`/project/${projectId}/data-collection`);
        } finally {
          setLoading(false);
        }
      };
      fetchSurvey();
    }
  }, [surveyId, projectId, token, navigate]);

  const applyTemplate = async (templateId) => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(`${API}/survey-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const template = res.data;
      
      // Generate new unique IDs for each question to avoid conflicts
      const questionsWithNewIds = template.questions.map(q => ({
        ...q,
        id: uuidv4()
      }));
      
      setTitle(template.name);
      setDescription(template.description);
      setQuestions(questionsWithNewIds);
      setTemplateModalOpen(false);
      toast.success(`Template "${template.name}" applied successfully!`);
    } catch (err) {
      toast.error('Failed to load template');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const addQuestion = (type) => {
    const newQuestion = {
      id: uuidv4(),
      type,
      question: '',
      required: true,
      options: type === 'multiple_choice' || type === 'dropdown' || type === 'ranked' || type === 'demographic' ? ['Option 1', 'Option 2'] : null,
      demographic_type: type === 'demographic' ? 'custom' : null,  // age, gender, education, income, custom
      scale_min: type === 'likert' ? 1 : null,
      scale_max: type === 'likert' ? 5 : null,
      scale_labels: type === 'likert' ? DEFAULT_LIKERT_LABELS : null,
      matrix_rows: type === 'matrix' ? ['Item 1', 'Item 2'] : null,
      matrix_columns: type === 'matrix' ? ['Poor', 'Fair', 'Good', 'Excellent'] : null
    };
    setQuestions([...questions, newQuestion]);
    setAddQuestionOpen(false);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const deleteQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const duplicateQuestion = (index) => {
    const original = questions[index];
    const duplicate = { ...original, id: uuidv4() };
    const updated = [...questions];
    updated.splice(index + 1, 0, duplicate);
    setQuestions(updated);
  };

  const addOption = (questionIndex) => {
    const updated = [...questions];
    const options = updated[questionIndex].options || [];
    updated[questionIndex].options = [...options, `Option ${options.length + 1}`];
    setQuestions(updated);
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex] = value;
    setQuestions(updated);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updated = [...questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    setQuestions(updated);
  };

  const handleSave = async (activate = false) => {
    if (!title.trim()) {
      toast.error('Please enter a survey title');
      return;
    }
    if (questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }
    if (questions.some(q => !q.question.trim())) {
      toast.error('Please fill in all question texts');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        title: title.trim(),
        description: description.trim(),
        questions,
        target_responses: targetResponses ? parseInt(targetResponses) : null
      };

      let response;
      if (surveyId) {
        await axios.put(`${API}/surveys/${surveyId}`, {
          ...payload,
          status: activate ? 'active' : 'draft'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        response = { data: { id: surveyId } };
      } else {
        response = await axios.post(`${API}/surveys`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (activate) {
          await axios.put(`${API}/surveys/${response.data.id}`, { status: 'active' }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }

      toast.success(activate ? 'Survey activated!' : 'Survey saved');
      navigate(`/project/${projectId}/data-collection`);
    } catch (err) {
      toast.error('Failed to save survey');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/project/${projectId}/data-collection`)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-slate-900">
                {surveyId ? 'Edit Survey' : 'Create Survey'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Template Button - Only show when creating new survey */}
              {!surveyId && (
                <Dialog open={templateModalOpen} onOpenChange={setTemplateModalOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid="use-template-btn">
                      <LayoutTemplate className="w-4 h-4 mr-2" />
                      Use Template
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                        Choose a Survey Template
                      </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-500 mt-1 mb-4">
                      Start with a pre-built template and customize it to your needs
                    </p>
                    {loadingTemplates ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-4">
                        {templates.map(template => {
                          const CategoryIcon = CATEGORY_ICONS[template.category] || CATEGORY_ICONS.default;
                          return (
                            <button
                              key={template.id}
                              onClick={() => applyTemplate(template.id)}
                              className="p-4 border border-slate-200 rounded-xl hover:border-indigo-400 hover:shadow-md transition-all text-left group"
                              data-testid={`template-${template.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition-colors">
                                  <CategoryIcon className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                                    {template.name}
                                  </h3>
                                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                                    {template.description}
                                  </p>
                                  <div className="flex items-center gap-3 mt-3">
                                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                                      {template.category}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                      {template.question_count} questions
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {templates.length === 0 && !loadingTemplates && (
                      <div className="text-center py-8 text-slate-500">
                        No templates available
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              )}
              <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </Button>
              <Button onClick={() => handleSave(true)} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                <Eye className="w-4 h-4 mr-2" />
                Save & Activate
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Template Suggestion Card - Show only when creating new and no questions yet */}
        {!surveyId && questions.length === 0 && templates.length > 0 && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white rounded-xl shadow-sm">
                <Sparkles className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Start with a Template</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Save time by choosing from {templates.length} pre-built survey templates designed for common research needs.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {templates.slice(0, 3).map(template => (
                    <button
                      key={template.id}
                      onClick={() => applyTemplate(template.id)}
                      className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                      data-testid={`quick-template-${template.id}`}
                    >
                      {template.name}
                    </button>
                  ))}
                  {templates.length > 3 && (
                    <button
                      onClick={() => setTemplateModalOpen(true)}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                    >
                      View All Templates
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Survey Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Survey Details</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Survey Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Customer Satisfaction Survey"
                className="mt-1"
                data-testid="survey-title-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the survey purpose..."
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={2}
              />
            </div>
            <div>
              <Label>Target Responses (optional)</Label>
              <Input
                type="number"
                value={targetResponses}
                onChange={(e) => setTargetResponses(e.target.value)}
                placeholder="e.g., 100"
                className="mt-1"
                min="1"
              />
              <p className="text-xs text-slate-500 mt-1">Survey will close after reaching this number</p>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Questions ({questions.length})</h2>
          </div>

          {questions.map((question, index) => (
            <QuestionEditor
              key={question.id}
              question={question}
              index={index}
              onUpdate={updateQuestion}
              onDelete={deleteQuestion}
              onDuplicate={duplicateQuestion}
              onAddOption={addOption}
              onUpdateOption={updateOption}
              onRemoveOption={removeOption}
            />
          ))}

          {/* Add Question Button */}
          <Dialog open={addQuestionOpen} onOpenChange={setAddQuestionOpen}>
            <DialogTrigger asChild>
              <button 
                className="w-full p-4 border-2 border-dashed border-slate-300 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2 text-slate-600 hover:text-indigo-600"
                data-testid="add-question-btn"
              >
                <Plus className="w-5 h-5" />
                Add Question
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Select Question Type</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {QUESTION_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => addQuestion(type.value)}
                    className="p-4 border border-slate-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors text-left"
                    data-testid={`add-${type.value}-btn`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <type.icon className="w-5 h-5 text-indigo-600" />
                      <span className="font-medium text-slate-900">{type.label}</span>
                    </div>
                    <p className="text-sm text-slate-500">{type.description}</p>
                  </button>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};

// Question Editor Component
const QuestionEditor = ({ question, index, onUpdate, onDelete, onDuplicate, onAddOption, onUpdateOption, onRemoveOption }) => {
  const typeInfo = QUESTION_TYPES.find(t => t.value === question.type);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`question-${index}`}>
      <div className="flex items-start gap-4">
        <div className="cursor-move text-slate-400">
          <GripVertical className="w-5 h-5" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
              {typeInfo?.label || question.type}
            </span>
            <span className="text-sm text-slate-500">Question {index + 1}</span>
            <div className="flex-1" />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={question.required}
                onChange={(e) => onUpdate(index, 'required', e.target.checked)}
                className="rounded border-slate-300"
              />
              Required
            </label>
          </div>

          <Input
            value={question.question}
            onChange={(e) => onUpdate(index, 'question', e.target.value)}
            placeholder="Enter your question..."
            className="mb-4 text-lg"
            data-testid={`question-text-${index}`}
          />

          {/* Multiple Choice / Dropdown / Ranked Options */}
          {['multiple_choice', 'dropdown', 'ranked'].includes(question.type) && (
            <div className="space-y-2 mb-4">
              <Label className="text-sm">Options</Label>
              {question.options?.map((option, optIdx) => (
                <div key={optIdx} className="flex items-center gap-2">
                  <span className="text-sm text-slate-400 w-6">{optIdx + 1}.</span>
                  <Input
                    value={option}
                    onChange={(e) => onUpdateOption(index, optIdx, e.target.value)}
                    placeholder={`Option ${optIdx + 1}`}
                    className="flex-1"
                  />
                  {question.options.length > 2 && (
                    <button 
                      onClick={() => onRemoveOption(index, optIdx)}
                      className="text-red-500 hover:text-red-600 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => onAddOption(index)}
                className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Option
              </button>
            </div>
          )}

          {/* Demographic Question Settings */}
          {question.type === 'demographic' && (
            <div className="space-y-4 mb-4">
              <div>
                <Label className="text-sm mb-2 block">Demographic Type</Label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(DEMOGRAPHIC_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => {
                        onUpdate(index, 'demographic_type', key);
                        if (key !== 'custom') {
                          onUpdate(index, 'question', preset.question);
                          onUpdate(index, 'options', [...preset.options]);
                        }
                      }}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        question.demographic_type === key 
                          ? 'bg-indigo-100 border-indigo-400 text-indigo-700' 
                          : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm">Options (select one)</Label>
                {question.options?.map((option, optIdx) => (
                  <div key={optIdx} className="flex items-center gap-2">
                    <Circle className="w-4 h-4 text-slate-300" />
                    <Input
                      value={option}
                      onChange={(e) => onUpdateOption(index, optIdx, e.target.value)}
                      placeholder={`Option ${optIdx + 1}`}
                      className="flex-1"
                    />
                    {question.options.length > 2 && (
                      <button 
                        onClick={() => onRemoveOption(index, optIdx)}
                        className="text-red-500 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => onAddOption(index)}
                  className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Option
                </button>
              </div>
            </div>
          )}

          {/* Likert Scale Settings */}
          {question.type === 'likert' && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-sm">Scale Range</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    value={question.scale_min}
                    onChange={(e) => onUpdate(index, 'scale_min', parseInt(e.target.value))}
                    className="w-20"
                    min="1"
                  />
                  <span className="text-slate-500">to</span>
                  <Input
                    type="number"
                    value={question.scale_max}
                    onChange={(e) => onUpdate(index, 'scale_max', parseInt(e.target.value))}
                    className="w-20"
                    min={question.scale_min + 1}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Preview</Label>
                <div className="flex items-center gap-2 mt-1">
                  {Array.from({ length: question.scale_max - question.scale_min + 1 }, (_, i) => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-slate-300 flex items-center justify-center text-xs font-medium">
                      {question.scale_min + i}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Matrix Settings */}
          {question.type === 'matrix' && (
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-sm">Rows (Items to rate)</Label>
                <div className="space-y-2 mt-1">
                  {question.matrix_rows?.map((row, rowIdx) => (
                    <div key={rowIdx} className="flex items-center gap-2">
                      <Input
                        value={row}
                        onChange={(e) => {
                          const newRows = [...question.matrix_rows];
                          newRows[rowIdx] = e.target.value;
                          onUpdate(index, 'matrix_rows', newRows);
                        }}
                        placeholder={`Item ${rowIdx + 1}`}
                        className="flex-1"
                      />
                      {question.matrix_rows.length > 1 && (
                        <button 
                          onClick={() => {
                            const newRows = question.matrix_rows.filter((_, i) => i !== rowIdx);
                            onUpdate(index, 'matrix_rows', newRows);
                          }}
                          className="text-red-500 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => onUpdate(index, 'matrix_rows', [...question.matrix_rows, `Item ${question.matrix_rows.length + 1}`])}
                    className="text-sm text-indigo-600 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Row
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-sm">Columns (Rating options)</Label>
                <div className="space-y-2 mt-1">
                  {question.matrix_columns?.map((col, colIdx) => (
                    <div key={colIdx} className="flex items-center gap-2">
                      <Input
                        value={col}
                        onChange={(e) => {
                          const newCols = [...question.matrix_columns];
                          newCols[colIdx] = e.target.value;
                          onUpdate(index, 'matrix_columns', newCols);
                        }}
                        placeholder={`Column ${colIdx + 1}`}
                        className="flex-1"
                      />
                      {question.matrix_columns.length > 2 && (
                        <button 
                          onClick={() => {
                            const newCols = question.matrix_columns.filter((_, i) => i !== colIdx);
                            onUpdate(index, 'matrix_columns', newCols);
                          }}
                          className="text-red-500 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => onUpdate(index, 'matrix_columns', [...question.matrix_columns, `Column ${question.matrix_columns.length + 1}`])}
                    className="text-sm text-indigo-600 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Column
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <button
            onClick={() => onDuplicate(index)}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(index)}
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyBuilderPage;
